import { User, LoginCredentials } from '../types';
import { account, storeSession, clearSession, getStoredUser, hasActiveSession } from './appwriteClient';

// Valid roles for this portal
const VALID_ROLES = ['admin', 'teacher'] as const;

// Role validation function
const validateUserRole = (role: string): role is 'admin' | 'teacher' => {
  return VALID_ROLES.includes(role as 'admin' | 'teacher');
};

export const login = async (credentials: LoginCredentials): Promise<User> => {
  try {
    // Check if there's an active session first
    const hasSession = await hasActiveSession();
    if (hasSession) {
      // Clear existing session
      try {
        await account.deleteSession('current');
        clearSession();
      } catch (error) {
        console.error('Failed to clear existing session:', error);
        throw new Error('Session already active. Please log out first.');
      }
    }

    // Create new session
    const session = await account.createEmailPasswordSession(credentials.email, credentials.password);
    const user = await account.get();
    
    const userRole = user.prefs?.role;
    
    // Validate user role
    if (!validateUserRole(userRole)) {
      // Log out the user immediately
      try {
        await account.deleteSession('current');
      } catch (error) {
        console.error('Failed to delete session after role validation:', error);
      }
      clearSession();
      
      throw new Error('INVALID_ROLE');
    }
    
    const userData: User = {
      id: user.$id,
      name: user.name,
      email: user.email,
      role: userRole,
      avatar: user.prefs?.avatar
    };
    
    // Store session and user data
    storeSession(session, userData);
    return userData;
  } catch (error) {
    console.error('Appwrite login error:', error);
    
    // Check if it's a role validation error
    if (error instanceof Error && error.message === 'INVALID_ROLE') {
      throw new Error('INVALID_ROLE');
    }
    
    // Check if it's a session creation error
    if (error instanceof Error && error.message.includes('session is active')) {
      throw new Error('Session already active. Please log out first.');
    }
    
    throw new Error('Invalid email or password');
  }
};

export const logout = async (): Promise<void> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Try Appwrite logout first
  try {
    await account.deleteSession('current');
  } catch (error) {
    console.error('Appwrite logout failed:', error);
  }
  
  // Clear session data
  clearSession();
};

export const getCurrentUser = async (): Promise<User | null> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return getStoredUser();
};